/**
 * Dashboard Service
 * =================
 *
 * Service for managing dashboard data, statistics, and metrics
 * Uses AngularFirestore for direct data access
 *
 * @version 1.0.0
 * @author BeFret Development Team
 */

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { getDisplayPrice } from '../core/utils/display-price';
import { Firestore, collection, collectionData } from '@angular/fire/firestore';
import { UnifiedShipment, UnifiedShipmentPhase } from '../core/models/unified-shipment.models';
import {
  DashboardStats,
  StatusCount,
  CountryRevenue,
  MonthlyRevenue,
  RecentShipment,
  QuickAction,
  STATUS_CONFIG,
  COUNTRY_NAMES,
  CHART_COLORS,
  MONTH_LABELS
} from '../models/dashboard.models';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  constructor(
    private firestore: Firestore
  ) {}

  /**
   * Get all active shipments (excluding drafts)
   */
  getAllActiveShipments(): Observable<UnifiedShipment[]> {
    return (collectionData(collection(this.firestore, 'shipments')) as Observable<UnifiedShipment[]>)
      .pipe(
        map(shipments => shipments.filter(s => s.phase?.current !== 'draft' as any)),
        shareReplay(1)
      );
  }

  /**
   * Get complete dashboard statistics
   */
  getDashboardStats(): Observable<DashboardStats> {
    return this.getAllActiveShipments().pipe(
      map(shipments => this.calculateDashboardStats(shipments)),
      catchError(error => {
        console.error('❌ [DASHBOARD SERVICE] Error calculating stats:', error);
        return of(this.getEmptyStats());
      })
    );
  }

  /**
   * #1430 — `getShipmentsByStatus` a été RETIRÉE ici.
   *
   * Elle filtrait `phase.current` sur un TROISIÈME vocabulaire — `pending`,
   * `warehouse_arrival`, `in_transit`, `out_for_delivery` — qui n'appartenait ni
   * à l'enum `ShipmentPhase` (ce que le backend écrit), ni à
   * `CLIENT_TIMELINE_PHASES` (ce que la frise affiche). Les branches `sent` et
   * `arrived` ne pouvaient donc rendre QUE des listes vides, quel que soit le
   * parc : aucune valeur de `phase.current` n'a jamais valu `in_transit` ni
   * `out_for_delivery`.
   *
   * Les `as any` de chacune de ses comparaisons avaient fait taire le
   * compilateur, qui aurait signalé exactement ça.
   *
   * Mesuré avant de retirer : **zéro appelant** dans `src/`, `functions/` et
   * `packages/` (templates compris). Un troisième vocabulaire mort est un piège
   * pour la prochaine personne qui cherchera comment filtrer par phase — elle le
   * recopierait en croyant qu'il fait autorité.
   */

  /**
   * Get shipments by country code
   */
  getShipmentsByCountry(countryCode: string): Observable<UnifiedShipment[]> {
    return this.getAllActiveShipments().pipe(
      map(shipments =>
        shipments.filter(s =>
          s.customerInfo?.sender?.phone?.prefix === countryCode ||
          s.customerInfo?.receiver?.phone?.prefix === countryCode
        )
      )
    );
  }

  /**
   * Calculate complete dashboard statistics
   */
  private calculateDashboardStats(shipments: UnifiedShipment[]): DashboardStats {
    const totalRevenue = this.calculateTotalRevenue(shipments);
    const averageShipmentValue = shipments.length > 0 ? totalRevenue / shipments.length : 0;

    return {
      totalShipments: shipments.length,
      totalRevenue: totalRevenue,
      averageShipmentValue: averageShipmentValue,
      byStatus: this.groupByStatus(shipments),
      byCountry: this.groupByCountry(shipments, totalRevenue),
      byMonth: this.groupByMonth(shipments, totalRevenue),
      recentShipments: this.getRecentShipments(shipments),
      topDestinations: this.getTopDestinations(shipments)
    };
  }

  /**
   * Calculate total revenue from shipments
   */
  private calculateTotalRevenue(shipments: UnifiedShipment[]): number {
    return shipments.reduce((sum, s) => {
      const amount = getDisplayPrice(s); // #374 prix payé, remise incluse
      return sum + amount;
    }, 0);
  }

  /**
   * Group shipments by status
   */
  private groupByStatus(shipments: UnifiedShipment[]): StatusCount[] {
    const statusMap = new Map<string, number>();

    shipments.forEach(s => {
      const status = (s.phase?.current as string) || 'unknown';
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    });

    const statusCounts: StatusCount[] = [];
    statusMap.forEach((count, status) => {
      const config = STATUS_CONFIG[status] || { labelKey: status, color: '#6c757d' };
      statusCounts.push({
        status: status,
        statusLabel: config.labelKey,
        count: count,
        percentage: (count / shipments.length) * 100,
        color: config.color
      });
    });

    return statusCounts.sort((a, b) => b.count - a.count);
  }

  /**
   * Group shipments by country with revenue
   */
  private groupByCountry(shipments: UnifiedShipment[], totalRevenue: number): CountryRevenue[] {
    const countryMap = new Map<string, { revenue: number; count: number }>();

    shipments.forEach(s => {
      const countryCode = s.customerInfo?.sender?.phone?.prefix || 'unknown';
      const revenue = getDisplayPrice(s); // #374 prix payé, remise incluse

      if (!countryMap.has(countryCode)) {
        countryMap.set(countryCode, { revenue: 0, count: 0 });
      }

      const data = countryMap.get(countryCode)!;
      data.revenue += revenue;
      data.count += 1;
    });

    const countryRevenues: CountryRevenue[] = [];
    let colorIndex = 0;

    countryMap.forEach((data, countryCode) => {
      countryRevenues.push({
        countryCode: countryCode,
        countryName: COUNTRY_NAMES[countryCode] || countryCode,
        revenue: data.revenue,
        shipmentCount: data.count,
        percentage: totalRevenue > 0 ? (data.revenue / totalRevenue) * 100 : 0,
        barColor: CHART_COLORS[colorIndex % CHART_COLORS.length]
      });
      colorIndex++;
    });

    // Sort by revenue (descending)
    return countryRevenues.sort((a, b) => b.revenue - a.revenue);
  }

  /**
   * Group shipments by month (last 12 months)
   */
  private groupByMonth(shipments: UnifiedShipment[], totalRevenue: number): MonthlyRevenue[] {
    const now = new Date();
    const monthlyData: MonthlyRevenue[] = [];

    // Generate last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthNumber = date.getMonth() + 1;
      const year = date.getFullYear();

      // Filter shipments for this month
      const monthShipments = shipments.filter(s => {
        const shipmentDate = this.getShipmentDate(s);
        return shipmentDate &&
               shipmentDate.getMonth() === date.getMonth() &&
               shipmentDate.getFullYear() === year;
      });

      const monthRevenue = monthShipments.reduce((sum, s) =>
        sum + getDisplayPrice(s), 0 // #374 prix payé, remise incluse
      );

      monthlyData.push({
        month: MONTH_LABELS[date.getMonth()],
        monthNumber: monthNumber,
        year: year,
        revenue: monthRevenue,
        shipmentCount: monthShipments.length,
        percentage: totalRevenue > 0 ? (monthRevenue / totalRevenue) * 100 : 0,
        displayLabel: `${MONTH_LABELS[date.getMonth()]} ${year}`
      });
    }

    return monthlyData;
  }

  /**
   * Get recent shipments (last 10)
   */
  private getRecentShipments(shipments: UnifiedShipment[]): RecentShipment[] {
    const sorted = [...shipments].sort((a, b) => {
      const dateA = this.getShipmentDate(a);
      const dateB = this.getShipmentDate(b);
      if (!dateA || !dateB) return 0;
      return dateB.getTime() - dateA.getTime();
    });

    return sorted.slice(0, 10).map(s => {
      const phaseStatus = (s.phase?.current as string) || 'unknown';
      return {
        trackingNumber: s.trackingNumber,
        senderName: s.customerInfo?.sender?.name || 'Unknown',
        receiverName: s.customerInfo?.receiver?.name || 'Unknown',
        destination: s.customerInfo?.receiver?.address?.city || 'Unknown',
        status: phaseStatus,
        statusLabel: STATUS_CONFIG[phaseStatus]?.labelKey || phaseStatus,
        createdAt: this.getShipmentDate(s) || new Date(),
        amount: getDisplayPrice(s) // #374 prix payé, remise incluse
      };
    });
  }

  /**
   * Get top destinations (cities)
   */
  private getTopDestinations(shipments: UnifiedShipment[]): string[] {
    const cityMap = new Map<string, number>();

    shipments.forEach(s => {
      const city = s.customerInfo?.receiver?.address?.city;
      if (city) {
        cityMap.set(city, (cityMap.get(city) || 0) + 1);
      }
    });

    const sorted = Array.from(cityMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(entry => entry[0]);

    return sorted;
  }

  /**
   * Get shipment creation date
   */
  private getShipmentDate(shipment: UnifiedShipment): Date | null {
    if (!shipment.timestamps?.createdAt) return null;

    const timestamp: any = shipment.timestamps.createdAt;
    if (timestamp instanceof Date) {
      return timestamp;
    } else if (typeof timestamp === 'object' && timestamp.toDate) {
      return timestamp.toDate();
    } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp);
    }

    return null;
  }

  /**
   * Get empty stats (fallback)
   */
  private getEmptyStats(): DashboardStats {
    return {
      totalShipments: 0,
      totalRevenue: 0,
      averageShipmentValue: 0,
      byStatus: [],
      byCountry: [],
      byMonth: [],
      recentShipments: [],
      topDestinations: []
    };
  }

  /**
   * Get quick action buttons configuration
   */
  getQuickActions(stats: DashboardStats): QuickAction[] {
    // Compteurs avec les nouveaux noms de timeline steps
    const pendingCount = (stats.byStatus.find(s => s.status === 'order')?.count || 0) +
                         (stats.byStatus.find(s => s.status === 'pending')?.count || 0);
    const receivedCount = (stats.byStatus.find(s => s.status === 'warehouse')?.count || 0) +
                          (stats.byStatus.find(s => s.status === 'collected_europe')?.count || 0);
    const sentCount = (stats.byStatus.find(s => s.status === 'befret_transit')?.count || 0) +
                      (stats.byStatus.find(s => s.status === 'in_transit')?.count || 0);
    const arrivedCount = (stats.byStatus.find(s => s.status === 'congo_distribution')?.count || 0) +
                         (stats.byStatus.find(s => s.status === 'out_for_delivery')?.count || 0);
    const deliveredCount = stats.byStatus.find(s => s.status === 'delivered')?.count || 0;

    return [
      {
        id: 'parcel-list',
        icon: 'list',
        labelKey: 'DASHBOARD.ACTIONS.PARCEL_LIST',
        route: '/parcel-list',
        type: 'primary',
        color: 'primary'
      },
      {
        id: 'pending',
        icon: 'schedule',
        labelKey: 'DASHBOARD.ACTIONS.RECEPTION_BEL',
        route: '/parcel-list',
        queryParams: { status: 'order' },
        type: 'status',
        badge: pendingCount > 0 ? { value: pendingCount, color: 'warn' } : undefined
      },
      {
        id: 'received',
        icon: 'move_to_inbox',
        labelKey: 'DASHBOARD.ACTIONS.SEND_DRC',
        route: '/parcel-list',
        queryParams: { status: 'warehouse' },
        type: 'status',
        badge: receivedCount > 0 ? { value: receivedCount, color: 'primary' } : undefined
      },
      {
        id: 'sent',
        icon: 'local_shipping',
        labelKey: 'DASHBOARD.ACTIONS.RECEPTION_DRC',
        route: '/parcel-list',
        queryParams: { status: 'befret_transit' },
        type: 'status',
        badge: sentCount > 0 ? { value: sentCount, color: 'accent' } : undefined
      },
      {
        id: 'arrived',
        icon: 'inventory',
        labelKey: 'DASHBOARD.ACTIONS.DELIVERIES',
        route: '/parcel-list',
        queryParams: { status: 'congo_distribution' },
        type: 'status',
        badge: arrivedCount > 0 ? { value: arrivedCount, color: 'accent' } : undefined
      },
      {
        id: 'promotions',
        icon: 'local_offer',
        labelKey: 'DASHBOARD.ACTIONS.PROMOTIONS',
        route: '/promotions-list',
        type: 'secondary'
      }
    ];
  }

  /**
   * Get status count by status name
   */
  getStatusCount(stats: DashboardStats, statusName: string): number {
    return stats.byStatus.find(s => s.status === statusName)?.count || 0;
  }

  /**
   * Format currency value
   */
  formatCurrency(value: number): string {
    return `€${value.toFixed(2)}`;
  }

  /**
   * Format percentage value
   */
  formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  }
}
